import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Paperclip, Import } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EntityLookup, type EntityLookupValue } from "@/components/shared/EntityLookup";
import { importarIdosos, montarResumo, type LinhaImportacaoIdoso, type ResumoImportacao } from "./api";

// Porte de app/controllers/importacao-idoso.controller.js +
// app/templates/importacao/importacao-idoso.html. Segunda rota a usar o
// <EntityLookup>. A tabela de resultado não é paginada no legado (é o
// array de resposta inteiro jogado no bootstrap-table local) — reproduzido
// com <Table> simples, sem DataTable/usePagedQuery.
const STATUS_BADGE: Record<LinhaImportacaoIdoso["status"], { label: string; className: string }> = {
  IMPORTADO: { label: "Importado", className: "border-transparent bg-green-100 text-green-800 hover:bg-green-100" },
  IMPORTADO_SEM_CPF: { label: "Sem CPF", className: "border-transparent bg-yellow-100 text-yellow-800 hover:bg-yellow-100" },
  DUPLICADO: { label: "Duplicado", className: "border-transparent bg-blue-100 text-blue-800 hover:bg-blue-100" },
  ERRO: { label: "Erro", className: "" },
};

function nomeLinha(dados: LinhaImportacaoIdoso["dados"]): string {
  if (!dados) return "";
  return dados.nome + (dados.sobrenome ? ` ${dados.sobrenome}` : "");
}

export default function ImportacaoIdoso() {
  const [instituicao, setInstituicao] = useState<EntityLookupValue | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [resultado, setResultado] = useState<LinhaImportacaoIdoso[] | null>(null);
  const [resumo, setResumo] = useState<ResumoImportacao | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const importar = useMutation({
    mutationFn: () => importarIdosos(instituicao!.id, arquivo!),
    onSuccess: (linhas) => {
      setResultado(linhas);
      const r = montarResumo(linhas);
      setResumo(r);
      if (r.erros > 0 || r.semCpf > 0 || r.duplicados > 0) {
        toast.warning(
          `${r.importados} importados, ${r.semCpf} sem CPF, ${r.duplicados} duplicados, ${r.erros} com erro`,
          { description: "Importação concluída com pendências" },
        );
      } else {
        toast.success(`${r.importados} idosos importados com sucesso`, { description: "Importação concluída" });
      }
    },
    onError: (error: unknown) => {
      const mensagem =
        (error as { response?: { data?: { mensagem?: string } } })?.response?.data?.mensagem ??
        "Falha ao processar o arquivo. Confira o layout da planilha.";
      toast.error(mensagem);
    },
  });

  function handleImportar() {
    if (!instituicao) {
      toast.warning("Selecione a instituição antes de importar");
      return;
    }
    if (!arquivo) {
      toast.warning("Selecione um arquivo antes de importar");
      return;
    }
    setResultado(null);
    setResumo(null);
    importar.mutate();
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold text-foreground">Importação de Idosos</h1>
      <p className="text-sm text-muted-foreground">
        Selecione a instituição, depois a planilha Excel (.xlsx) com os dados dos idosos, e clique em Importar.
      </p>

      <div className="max-w-sm">
        <EntityLookup
          id="instituicao"
          label="Instituição"
          entityPath="instituicao/Instituicao"
          searchByCodeAndName
          displayCode
          value={instituicao}
          onChange={setInstituicao}
          required
        />
      </div>

      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
          <Paperclip className="mr-2 h-4 w-4" /> Selecionar planilha
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
        />
        <span className="text-sm text-muted-foreground">{arquivo?.name ?? "Nenhum arquivo selecionado"}</span>
      </div>

      <Button onClick={handleImportar} disabled={importar.isPending}>
        <Import className="mr-2 h-4 w-4" />
        {importar.isPending ? "Processando arquivo..." : "Importar"}
      </Button>

      {resumo && (
        <p className="text-sm">
          <strong>Resultado:</strong> {resumo.total} linha(s) processada(s) — {resumo.importados} importado(s),{" "}
          {resumo.semCpf} sem CPF (CPF inválido informado), {resumo.duplicados} duplicado(s), {resumo.erros} com erro
        </p>
      )}

      {resultado && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Linha</TableHead>
              <TableHead>Idoso</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Detalhes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {resultado.map((linha) => (
              <TableRow key={linha.linha}>
                <TableCell>{linha.linha}</TableCell>
                <TableCell>{nomeLinha(linha.dados)}</TableCell>
                <TableCell>
                  <Badge className={STATUS_BADGE[linha.status].className} variant={linha.status === "ERRO" ? "destructive" : "outline"}>
                    {STATUS_BADGE[linha.status].label}
                  </Badge>
                </TableCell>
                <TableCell>{linha.mensagem}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
